/**
 * ============================================================
 * PEETALYA WISHLIST SYSTEM v2
 * Vanilla ES6+ | localStorage | Zero dependencies
 * Dawn Theme 15+ | Shopify OS 2.0
 * ============================================================
 */

'use strict';

/* ============================================================
   UTILITIES
   ============================================================ */

const formatPrice = (price) => {
  const str = String(price);
  if (str.includes('₹') || str.includes('.')) return str;
  const num = parseInt(str, 10);
  if (isNaN(num)) return str;
  return '₹\u202f' + (num / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const escapeHtml = (str) => {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return String(str || '').replace(/[&<>"']/g, (c) => map[c]);
};

/* ============================================================
   STORE
   ============================================================ */

const WishlistStore = (() => {
  const KEY = 'peetalya_wishlist_v2';

  const getAll = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; }
    catch { return []; }
  };

  const _save = (items) => localStorage.setItem(KEY, JSON.stringify(items));

  const add = (product) => {
    const items = getAll();
    if (items.some((i) => String(i.id) === String(product.id))) return false;
    items.push({ ...product, addedAt: Date.now() });
    _save(items);
    return true;
  };

  const remove = (id) => _save(getAll().filter((i) => String(i.id) !== String(id)));

  const toggle = (product) => {
    if (has(product.id)) { remove(product.id); return false; }
    add(product); return true;
  };

  const has  = (id) => getAll().some((i) => String(i.id) === String(id));
  const count = () => getAll().length;

  return { getAll, add, remove, toggle, has, count };
})();

/* ============================================================
   EVENT BUS
   ============================================================ */

const WishlistEvents = (() => {
  const listeners = {};
  const on   = (event, fn) => { (listeners[event] = listeners[event] || []).push(fn); };
  const emit = (event, data) => { (listeners[event] || []).forEach((fn) => fn(data)); };
  return { on, emit };
})();

/* ============================================================
   COUNTER
   ============================================================ */

const WishlistCounter = (() => {
  const update = () => {
    const n = WishlistStore.count();
    document.querySelectorAll('[data-pw-counter]').forEach((el) => {
      el.textContent = n > 0 ? n : '';
      el.setAttribute('data-count', n);
    });
    document.querySelectorAll('[data-pw-header-icon]').forEach((btn) => {
      btn.classList.toggle('pw-has-items', n > 0);
      btn.setAttribute('aria-label', `Wishlist${n > 0 ? ` (${n})` : ''}`);
    });
  };

  const init = () => { update(); WishlistEvents.on('change', update); };
  return { init };
})();



/* ============================================================
   BUTTONS
   ============================================================ */

const WishlistButtons = (() => {

  const syncButton = (btn) => {
    const id = btn.dataset.productId;
    if (!id) return;
    const active = WishlistStore.has(id);
    btn.setAttribute('aria-pressed', String(active));
    const label = btn.querySelector('[data-pw-label]');
    if (label) {
      label.textContent = active
        ? 'Saved'
        : btn.classList.contains('pw-pdp-btn') ? 'Save for Later' : 'Add to wishlist';
    }
  };

  const syncAll = () => document.querySelectorAll('[data-pw-btn]').forEach(syncButton);

  const pulse = (btn) => {
    btn.classList.remove('pw-pulse');
    void btn.offsetWidth;
    btn.classList.add('pw-pulse');
    btn.addEventListener('animationend', () => btn.classList.remove('pw-pulse'), { once: true });
  };

  // Single delegated handler — works for every [data-pw-btn], whether it
  // exists at page load or is injected later (related products, quick
  // view, predictive search, infinite scroll, filter re-renders, etc).
  const handleClick = (e) => {
    const btn = e.target.closest('[data-pw-btn]');
    if (!btn) return;
    e.stopPropagation();

    const product = {
      id:        btn.dataset.productId,
      handle:    btn.dataset.handle,
      title:     btn.dataset.title,
      image:     btn.dataset.image,
      price:     btn.dataset.price,
      variant:   btn.dataset.variant   || '',
      variantId: btn.dataset.variantId || '',
      url:       btn.dataset.url,
    };

    WishlistStore.toggle(product);
    pulse(btn);
    syncButton(btn);
    WishlistEvents.emit('change', { id: product.id });
  };

  // Related products (and any other section that swaps in HTML via
  // fetch + innerHTML instead of Shopify's section-reload event) never
  // fires shopify:section:load, so buttons rendered that way used to be
  // both unbound and out of sync with saved state. This observer catches
  // any [data-pw-btn] added to the DOM by anything, at any time, and
  // syncs its visual state immediately. Clicks are already covered by
  // the delegated listener above, so no rebinding step is needed.
  const observeNewButtons = () => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.('[data-pw-btn]')) syncButton(node);
          node.querySelectorAll?.('[data-pw-btn]').forEach(syncButton);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    document.addEventListener('click', handleClick);

    syncAll();
    observeNewButtons();

    document.addEventListener('variant:changed', (e) => {
      const variant = e.detail?.variant;
      if (!variant) return;
      document.querySelectorAll('.pw-pdp-btn[data-pw-btn]').forEach((btn) => {
        btn.dataset.variantId = variant.id;
        btn.dataset.variant   = variant.title !== 'Default Title' ? variant.title : '';
        btn.dataset.price     = variant.price;
        syncButton(btn);
      });
    });

    // Kept for the theme editor's section-reload path; harmless no-op
    // elsewhere since the MutationObserver above already covers it.
    document.addEventListener('shopify:section:load', syncAll);
    WishlistEvents.on('change', syncAll);
  };

  return { init };
})();


/* ============================================================
   DRAWER
   ============================================================ */

const WishlistDrawer = (() => {
  let drawerEl, overlayEl, bodyEl, footerEl;
  let isOpen = false;

  const renderItem = (item) => `
    <li class="pw-drawer-item" role="listitem" data-item-id="${escapeHtml(item.id)}">
      <a href="${escapeHtml(item.url)}" tabindex="-1" aria-hidden="true">
        <img
          class="pw-drawer-item-img"
          src="${escapeHtml(item.image)}"
          alt="${escapeHtml(item.title)}"
          loading="lazy"
          width="72"
          height="72"
        >
      </a>
      <div class="pw-drawer-item-info">
        <a href="${escapeHtml(item.url)}" class="pw-drawer-item-title">${escapeHtml(item.title)}</a>
        ${item.variant && item.variant !== 'Default Title'
          ? `<span class="pw-drawer-item-variant">${escapeHtml(item.variant)}</span>`
          : ''}
        <span class="pw-drawer-item-price">${formatPrice(item.price)}</span>
      </div>
      <button
        class="pw-drawer-item-remove"
        data-pw-drawer-remove="${escapeHtml(item.id)}"
        aria-label="Remove ${escapeHtml(item.title)} from wishlist"
        type="button"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </li>`;

  const renderEmpty = () => `
    <div class="pw-drawer-empty">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
      <p class="pw-drawer-empty-title">Your wishlist is empty</p>
      <p class="pw-drawer-empty-sub">Save timeless pieces you love<br>and revisit them anytime.</p>
    </div>`;

  const render = () => {
    if (!bodyEl) return;
    const items = WishlistStore.getAll();
    const isEmpty = items.length === 0;
    bodyEl.innerHTML = isEmpty
      ? renderEmpty()
      : `<ul class="pw-drawer-list" role="list">${items.map(renderItem).join('')}</ul>`;
    if (footerEl) footerEl.classList.toggle('pw-hidden', isEmpty);
  };

  const open = () => {
    if (!drawerEl) return;
    render();
    requestAnimationFrame(() => {
      drawerEl.classList.add('pw-open');
      overlayEl?.classList.add('pw-open');
    });
    document.body.style.overflow = 'hidden';
    isOpen = true;
    drawerEl.setAttribute('aria-hidden', 'false');
    drawerEl.querySelector('[data-pw-close-drawer]')?.focus();
  };

  const close = () => {
    if (!drawerEl) return;
    drawerEl.classList.remove('pw-open');
    overlayEl?.classList.remove('pw-open');
    document.body.style.overflow = '';
    isOpen = false;
    drawerEl.setAttribute('aria-hidden', 'true');
    document.querySelector('[data-pw-open-drawer]')?.focus();
  };

  const handleRemove = (e) => {
    const btn = e.target.closest('[data-pw-drawer-remove]');
    if (!btn) return;
    const id = btn.dataset.pwDrawerRemove;
    const item = btn.closest('.pw-drawer-item');
    if (item) {
      item.style.transition = 'opacity 220ms ease, transform 220ms ease';
      item.style.opacity = '0';
      item.style.transform = 'translateX(12px)';
      setTimeout(() => {
        WishlistStore.remove(id);
        WishlistEvents.emit('change', { id });
        render();
      }, 220);
    } else {
      WishlistStore.remove(id);
      WishlistEvents.emit('change', { id });
      render();
    }
  };

  /* Focus trap — keeps keyboard navigation inside open drawer */
  const handleKeydown = (e) => {
    if (!isOpen || !drawerEl) return;
    const focusable = [...drawerEl.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])')];
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    if (e.key === 'Escape') close();
  };

  const init = () => {
    drawerEl  = document.getElementById('pw-drawer');
    overlayEl = document.getElementById('pw-drawer-overlay');
    bodyEl    = document.getElementById('pw-drawer-body');
    footerEl  = document.getElementById('pw-drawer-footer');
    if (!drawerEl) return;

    document.addEventListener('click', (e) => { if (e.target.closest('[data-pw-open-drawer]')) open(); });
    document.addEventListener('click', (e) => { if (e.target.closest('[data-pw-close-drawer]')) close(); });
    overlayEl?.addEventListener('click', close);
    bodyEl?.addEventListener('click', handleRemove);
    document.addEventListener('keydown', handleKeydown);

    // Only re-render if drawer is currently open
    WishlistEvents.on('change', () => { if (isOpen) render(); });

    // Cross-tab sync via storage event
    window.addEventListener('storage', (e) => {
      if (e.key === 'peetalya_wishlist_v2') WishlistEvents.emit('change', {});
    });
  };

  return { init, open, close };
})();

/* ============================================================
   WISHLIST PAGE
   ============================================================ */

const WishlistPage = (() => {
  let gridEl, emptyEl;

  const renderCard = (item) => `
    <article class="pw-product-card" data-item-id="${escapeHtml(item.id)}">
      <div class="pw-product-img-wrap">
        <a href="${escapeHtml(item.url)}" aria-label="View ${escapeHtml(item.title)}">
          <img
            class="pw-product-img"
            src="${escapeHtml(item.image)}"
            alt="${escapeHtml(item.title)}"
            loading="lazy"
            width="400"
            height="533"
          >
        </a>
        <button
          class="pw-btn pw-product-remove-btn"
          aria-pressed="true"
          aria-label="Remove ${escapeHtml(item.title)} from wishlist"
          data-pw-page-remove="${escapeHtml(item.id)}"
          type="button"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" stroke-width="1.5" fill="currentColor" stroke="currentColor">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
        </button>
      </div>
      <div class="pw-product-info">
        <a href="${escapeHtml(item.url)}" class="pw-product-title">${escapeHtml(item.title)}</a>
        ${item.variant && item.variant !== 'Default Title'
          ? `<span class="pw-product-variant">${escapeHtml(item.variant)}</span>`
          : ''}
        <span class="pw-product-price">${formatPrice(item.price)}</span>
        <div class="pw-product-actions">
          ${item.variantId
            ? `<button class="pw-product-atc" data-pw-atc="${escapeHtml(item.id)}" data-variant-id="${escapeHtml(item.variantId)}" type="button">Add to Cart</button>`
            : `<a href="${escapeHtml(item.url)}" class="pw-product-atc">Select Options</a>`
          }
          <a href="${escapeHtml(item.url)}" class="pw-product-view">View Product</a>
        </div>
      </div>
    </article>`;

  const showSkeleton = () => {
    if (!gridEl) return;
    gridEl.innerHTML = Array(4).fill(`
      <div class="pw-skeleton-card">
        <div class="pw-skeleton pw-skeleton-img"></div>
        <div class="pw-skeleton pw-skeleton-title"></div>
        <div class="pw-skeleton pw-skeleton-price"></div>
      </div>`).join('');
    gridEl.classList.remove('pw-hidden');
    emptyEl?.classList.add('pw-hidden');
  };

  const render = () => {
    if (!gridEl) return;
    const items = WishlistStore.getAll();
    if (items.length === 0) {
      gridEl.classList.add('pw-hidden');
      emptyEl?.classList.remove('pw-hidden');
    } else {
      gridEl.classList.remove('pw-hidden');
      emptyEl?.classList.add('pw-hidden');
      gridEl.innerHTML = items.map(renderCard).join('');
    }
  };

  const handleRemove = (e) => {
    const btn = e.target.closest('[data-pw-page-remove]');
    if (!btn) return;
    const id = btn.dataset.pwPageRemove;
    const card = btn.closest('.pw-product-card');
    if (card) {
      card.style.transition = 'opacity 300ms ease, transform 300ms ease';
      card.style.opacity = '0';
      card.style.transform = 'scale(0.97)';
      setTimeout(() => {
        WishlistStore.remove(id);
        WishlistEvents.emit('change', { id });
        render();
      }, 300);
    } else {
      WishlistStore.remove(id);
      WishlistEvents.emit('change', { id });
      render();
    }
  };

  const handleAtc = async (e) => {
    const btn = e.target.closest('[data-pw-atc]');
    if (!btn || btn.tagName === 'A') return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });

      if (res.ok) {
        btn.textContent = 'Added ✓';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
          });
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2500);
      } else {
        const err = await res.json().catch(() => ({}));
        btn.textContent = err.description || 'Unavailable';
        setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2500);
      }
    } catch {
      btn.textContent = original;
      btn.disabled = false;
    }
  };

  const init = () => {
    gridEl  = document.getElementById('pw-page-grid');
    emptyEl = document.getElementById('pw-page-empty');
    if (!gridEl) return;

    showSkeleton();
    requestAnimationFrame(render);

    gridEl.addEventListener('click', handleRemove);
    gridEl.addEventListener('click', handleAtc);
    WishlistEvents.on('change', render);
  };

  return { init };
})();

/* ============================================================
   SHARE WISHLIST
   ============================================================ */

const WishlistShare = (() => {

  const PARAM = 'pw_share';

  /**
   * Encode wishlist items into a compact base64 URL param.
   * Stores only: id, handle, title, image, price, variantId, url
   */
  const encode = (items) => {
    const compact = items.map((i) => ({
      i: i.id,
      h: i.handle,
      t: i.title,
      m: i.image,
      p: i.price,
      v: i.variantId || '',
      u: i.url,
      n: i.variant || '',
    }));
    return btoa(unescape(encodeURIComponent(JSON.stringify(compact))));
  };

  /**
   * Decode a shared URL param back into wishlist items.
   */
  const decode = (str) => {
    try {
      const raw = JSON.parse(decodeURIComponent(escape(atob(str))));
      return raw.map((i) => ({
        id:        i.i,
        handle:    i.h,
        title:     i.t,
        image:     i.m,
        price:     i.p,
        variantId: i.v,
        url:       i.u,
        variant:   i.n,
      }));
    } catch {
      return null;
    }
  };

  /**
   * Build the shareable URL for the current wishlist.
   */
  const buildShareUrl = () => {
    const items = WishlistStore.getAll();
    if (!items.length) return null;
    const encoded = encode(items);
    const url = new URL(window.location.origin + '/pages/wishlist');
    url.searchParams.set(PARAM, encoded);
    return url.toString();
  };

  /**
   * Share via Web Share API (mobile) or copy to clipboard (desktop).
   * @param {HTMLElement} btn — the share button element
   */
  const share = async (btn) => {
    const url = buildShareUrl();
    if (!url) return;

    const originalText = btn.textContent;

    // Web Share API — native mobile share sheet
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My Peetalya Wishlist',
          text:  'Discover the pieces I\'ve saved from Peetalya — heritage metalware in brass, copper & kansa.',
          url,
        });
        return;
      } catch {
        // User cancelled — do nothing
        return;
      }
    }

    // Fallback — copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      btn.textContent = 'Link Copied ✓';
      btn.classList.add('pw-share-copied');
      setTimeout(() => {
        btn.textContent = originalText;
        btn.classList.remove('pw-share-copied');
      }, 2500);
    } catch {
      // Final fallback for older browsers
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      btn.textContent = 'Link Copied ✓';
      setTimeout(() => { btn.textContent = originalText; }, 2500);
    }
  };

  /**
   * Check if current page is a shared wishlist view.
   * If so, render shared items instead of localStorage items.
   */
  const checkSharedView = () => {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get(PARAM);
    if (!encoded) return false;

    const items = decode(encoded);
    if (!items || !items.length) return false;

    // Mark page as shared view
    document.documentElement.setAttribute('data-pw-shared', 'true');

    // Render shared items into the page grid
    const gridEl  = document.getElementById('pw-page-grid');
    const emptyEl = document.getElementById('pw-page-empty');
    const headerEl = document.querySelector('.pw-page-header');

    if (headerEl) {
      // Update header to show shared context
      const eyebrow = headerEl.querySelector('.pw-page-eyebrow');
      const subtitle = headerEl.querySelector('.pw-page-subtitle');
      if (eyebrow) eyebrow.textContent = 'Shared Wishlist';
      if (subtitle) subtitle.textContent = 'A curated selection shared with you.';
    }

    if (!gridEl) return true;

    // Render shared items (read-only — no remove button, add to cart only)
    gridEl.innerHTML = items.map((item) => `
      <article class="pw-product-card" data-item-id="${escapeHtml(item.id)}">
        <div class="pw-product-img-wrap">
          <a href="${escapeHtml(item.url)}" aria-label="View ${escapeHtml(item.title)}">
            <img
              class="pw-product-img"
              src="${escapeHtml(item.image)}"
              alt="${escapeHtml(item.title)}"
              loading="lazy"
              width="400"
              height="533"
            >
          </a>
        </div>
        <div class="pw-product-info">
          <a href="${escapeHtml(item.url)}" class="pw-product-title">${escapeHtml(item.title)}</a>
          ${item.variant && item.variant !== 'Default Title'
            ? `<span class="pw-product-variant">${escapeHtml(item.variant)}</span>`
            : ''}
          <span class="pw-product-price">${formatPrice(item.price)}</span>
          <div class="pw-product-actions">
            ${item.variantId
              ? `<button class="pw-product-atc" data-pw-atc="${escapeHtml(item.id)}" data-variant-id="${escapeHtml(item.variantId)}" type="button">Add to Cart</button>`
              : `<a href="${escapeHtml(item.url)}" class="pw-product-atc">View Product</a>`
            }
            <a href="${escapeHtml(item.url)}" class="pw-product-view">View Product</a>
          </div>
        </div>
      </article>`).join('');

    gridEl.classList.remove('pw-hidden');
    emptyEl?.classList.add('pw-hidden');

    // Show save-to-my-wishlist banner
    const banner = document.getElementById('pw-shared-banner');
    if (banner) banner.classList.remove('pw-hidden');

    return true;
  };

  /**
   * Save all shared items into the visitor's own wishlist.
   */
  const saveSharedToWishlist = () => {
    const params = new URLSearchParams(window.location.search);
    const encoded = params.get(PARAM);
    if (!encoded) return;
    const items = decode(encoded);
    if (!items) return;
    items.forEach((item) => WishlistStore.add(item));
    WishlistEvents.emit('change', {});

    const btn = document.getElementById('pw-save-shared-btn');
    if (btn) {
      btn.textContent = 'Saved to Your Wishlist ✓';
      btn.disabled = true;
    }
  };

  const init = () => {
    // Share button click
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pw-share]');
      if (btn) share(btn);
    });

    // Save shared wishlist button
    document.addEventListener('click', (e) => {
      if (e.target.closest('#pw-save-shared-btn')) saveSharedToWishlist();
    });
  };

  return { init, checkSharedView, buildShareUrl };
})();

/* ============================================================
   BOOT
   ============================================================ */
document.addEventListener('DOMContentLoaded', () => {
  WishlistCounter.init();
  WishlistButtons.init();
  WishlistDrawer.init();
  WishlistShare.init();

  // Check for shared wishlist URL before rendering page
  const isSharedView = WishlistShare.checkSharedView();
  if (!isSharedView) {
    WishlistPage.init();
  } else {
    // Still init ATC on shared page
    const gridEl = document.getElementById('pw-page-grid');
    if (gridEl) {
      gridEl.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-pw-atc]');
        if (!btn || btn.tagName === 'A') return;
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
          });
          if (res.ok) {
            btn.textContent = 'Added ✓';
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            setTimeout(() => { btn.textContent = 'Add to Cart'; btn.disabled = false; }, 2500);
          }
        } catch {
          btn.disabled = false;
        }
      });
    }
  }

  // Show share button when wishlist has items
  const shareWrap = document.getElementById('pw-share-wrap');
  if (shareWrap) {
    const toggleShare = () => {
      shareWrap.classList.toggle('pw-hidden', WishlistStore.count() === 0);
    };
    toggleShare();
    WishlistEvents.on('change', toggleShare);
  }
});